import React from "react";
import { KNOWLEDGE_PAGES } from "@/lib/knowledge/pages";
import { Link } from "@/components/common/Link";
import { ArrowRight, BookOpen, Layers, CheckCircle2 } from "lucide-react";
interface PhaseOneDetail {
  id: string;
  tasks: string;
}
const PHASE_ONE_DETAILS: Record<string, PhaseOneDetail> = {
  philosophy: {
    id: "philosophy",
    tasks: "Clarify ambiguous concepts, conduct structured thought experiments (Ship of Theseus, Experience Machine), and apply reflective equilibrium to balance intuitions with principles."
  },
  logic: {
    id: "logic",
    tasks: "Construct propositional truth tables, distinguish formal validity from empirical soundness, and test syllogistic arguments against deductive rules."
  },
  critical: {
    id: "critical",
    tasks: "Dissect convoluted prose into explicit premises and conclusions, reveal unstated assumptions, and steelman adversarial claims before evaluating them."
  },
  fallacies: {
    id: "fallacies",
    tasks: "Diagnose structural and informal fallacies across realistic case specimens, explore base-rate neglect, and practice distinguishing genuine errors from rhetorical sleight-of-hand."
  },
  epistemology: {
    id: "epistemology",
    tasks: "Investigate the classical justified-true-belief definition of knowledge, analyze Gettier counterexamples, evaluate foundationalism vs. coherentism, and confront skepticism."
  },
  glossary: {
    id: "glossary",
    tasks: "Search and cross-reference foundational epistemology terms, review formal citations to seminal papers, and examine interconnected conceptual definitions."
  }
};
export function PhaseOneOverview() {
  const phaseOnePages = KNOWLEDGE_PAGES.filter(p => p.id !== "hub");
  return <section id="phase-one-overview" className="w-full scroll-mt-24 min-w-0">
      {/* Header */}
      <div className="mb-8">
        <span className="ql-eyebrow mb-2">CURRICULUM ARCHITECTURE</span>
        <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground">
          What's Inside Phase One
        </h2>
        <p className="font-sans text-sm sm:text-base text-muted-foreground mt-1 max-w-2xl">
          Six deep inquiry laboratories are fully operational today. Review what you will actually investigate in each module before entering.
        </p>
      </div>

      {/* Six Phase One Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 min-w-0">
        {phaseOnePages.map(page => {
        const detail = PHASE_ONE_DETAILS[page.id];
        return <div key={page.id} className="ql-glass p-6 rounded-2xl border border-border flex flex-col justify-between h-full min-h-[160px] sm:min-h-[240px] hover:border-primary/60 transition-all min-w-0">
              <div className="min-w-0">
                <div className="flex items-center justify-between gap-2 mb-3 min-w-0 flex-wrap">
                  <span className="font-mono text-xs font-bold text-primary uppercase tracking-wider shrink-0">
                    MODULE // {page.id}
                  </span>
                  <span className="inline-flex items-center gap-1 font-mono text-[0.65rem] text-emerald-400 border border-emerald-500/30 bg-emerald-950/40 px-2 py-0.5 rounded-full shrink-0">
                    <CheckCircle2 className="w-2.5 h-2.5" />
                    Open Lab
                  </span>
                </div>

                <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground mb-2 break-words leading-snug min-w-0">
                  {page.title}
                </h3>

                <p className="font-sans text-xs sm:text-sm text-foreground/80 leading-relaxed mb-3 break-words min-w-0">
                  {page.blurb}
                </p>

                {detail && <p className="font-sans text-xs text-muted-foreground leading-relaxed pt-2 border-t border-border/40 break-words min-w-0">
                    <strong className="text-foreground/90 font-medium font-serif">What you will do: </strong>
                    {detail.tasks}
                  </p>}
              </div>

              <div className="mt-5 pt-3 border-t border-border/40 min-w-0">
                <Link to={page.route} className="ql-button-secondary w-full flex items-center justify-center gap-2 py-2.5 px-4 text-xs sm:text-sm font-serif font-semibold min-h-[44px]">
                  <span className="break-words">Enter {page.title}</span>
                  <ArrowRight className="w-4 h-4 text-primary shrink-0" />
                </Link>
              </div>
            </div>;
      })}
      </div>

      {/* Note about later phases */}
      <div className="mt-8 p-5 sm:p-6 rounded-xl border border-border/50 bg-card/40 flex items-start gap-3.5 min-w-0">
        <Layers className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
        <div className="space-y-1 text-xs sm:text-sm text-muted-foreground min-w-0">
          <h4 className="font-serif font-bold text-foreground">
            Subsequent Investigation Phases
          </h4>
          <p className="leading-relaxed break-words">
            The remaining domains—scientific reasoning, cognitive biases, probability and uncertainty, language and semantics, normative ethics, decision theory, misinformation networks, and the personal workbench—are planned for later phases. On the Knowledge Map above, they are marked as "coming later" and do not link to unbuilt pages.
          </p>
        </div>
      </div>
    </section>;
}